import React, { useEffect, useState, useContext, useRef, useImperativeHandle } from "react";
import { Link, useLocation } from "react-router-dom";
import Backdrop from "@mui/material/Backdrop";
import CircularProgress from "@mui/material/CircularProgress";

import api from "services/api";

import { Card } from "@mui/material";
import Grid from "@mui/material/Grid";

import Tooltip from "@mui/material/Tooltip";
import Pagination from "@mui/material/Pagination";

import SuiBadge from "components/SuiBadge";
import SuiTypography from "components/SuiTypography";
import SuiBadgeDot from "components/SuiBadgeDot";
import SuiBox from "components/SuiBox";
import SuiAvatar from "components/SuiAvatar";
import SuiSelect from "components/SuiSelect";
import SuiInput from "components/SuiInput";
import SuiButton from "components/SuiButton";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";

import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ImageIcon from "@mui/icons-material/Image";

import { capitalCase } from "capital-case";
import { AccountContext } from "context/account";
import CategoryContext from "context/category-context";

import Prompt from "components/UpStyled/Prompt";
import toast from "react-hot-toast";
import moment from "moment";
import qs from "query-string";
import { Empty } from "antd";

import { filterParser, sortParser, compare } from "utils/helper";
import { stylesSortOptions, seasonOptions } from "utils/data";
import {
  canResizeImageUrl,
  getStyleImages,
  getFileImage,
  getOnLineTechPackImages,
} from "utils/imageHelper";

import ImageCardWrapper from "components/UpStyled/ImageCardWrapper";
import { collectionSortOptions } from "utils/data";

const MAX_IMAGE_COUNT = 8;

const ImportSingleCollectionModal = React.forwardRef(
  (
    {
      visible,
      handleClose,
      handleOk,
      isEdit = false,
      categoryTree = [],
      collection,
      lockCollection,
      handleRefresh = () => {},
      slot = "col_0",
    },
    ref
  ) => {
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);

    const [selectedCollection, setSelectedCollection] = useState({});
    const [selectedImg, setSelectedImg] = useState({});
    const [selectedStyleImgs, setSelectedStyleImgs] = useState([]);

    const [collectionList, setCollectionList] = useState([]);
    const [filteredCollectionList, setFilteredCollectionList] = useState([]);
    const [totalNumber, setTotalNumber] = useState(0);
    const [orderOpt, setOrderOpt] = useState(collectionSortOptions[4]);
    const [searchStr, setSearchStr] = useState("");
    const [collectionDetailVisible, setCollectionDetailVisible] = useState(false);
    const [collectionImages, setCollectionImages] = useState([]);

    const metaFilesKey = ["imageFiles"];

    const account = useContext(AccountContext);

    useImperativeHandle(
      ref,
      () => ({
        selectedCollection,
        setSelectedCollection,
      }),
      [selectedCollection]
    );

    useEffect(() => {
      if (visible) {
        if (collection && lockCollection) {
          setSelectedCollection(collection);
          retreiveCollectionImages(collection);
          setCollectionDetailVisible(true);

          let portfolioData = account?.metadata?.portfolio_content || {}; //user portfolio data
          let collectionData = portfolioData[slot] || [];
          const colImages = collectionData.filter((x) => x?.cid == collection?.id) || [];

          setSelectedStyleImgs(colImages);
        } else {
          retrieveCollections();
        }
      }
    }, [visible]);

    const retrieveCollections = async (sort = "") => {
      try {
        const { data } = await api.collections.list({ order: "-updated_at" });

        let collections = data["collections"] ? data["collections"] : [];

        collections = collections.map((x) => ({
          ...x,
          season: x?.metadata?.season?.value || "",
          year: x?.metadata?.year || "",
        }));
        setCollectionList(collections);
        setTotalNumber(collections.length);

        //filter based on pagination
        const sliceStart = pageSize * (page - 1);
        const sliceEnd =
          pageSize * page > collections.length ? collections.length : pageSize * page;
        const newFilteredList = collections.slice(sliceStart, sliceEnd);

        setFilteredCollectionList(newFilteredList);
      } catch (err) {
        toast.error(`Retrieve collection list failed`);
        console.log(err);
      }
    };

    const handleFilterCollections = (page, pageSize, str = searchStr) => {
      const copyProjects = [...collectionList];
      let filteredList = copyProjects; //all projects

      if (str) {
        const strUpper = str.toUpperCase();
        const searchFields = ["title", "description"];
        filteredList = filteredList.filter((x) => {
          let result = false;
          for (let i = 0; i < searchFields.length; i++) {
            if (x[searchFields[i]]) {
              if (typeof x[searchFields[i]] == "string") {
                if (x[searchFields[i]].toUpperCase().includes(strUpper)) {
                  result = true;
                }
              } else if (typeof x[searchFields[i]] == "object") {
                const text = JSON.stringify(x[searchFields[i]]);
                if (text.toUpperCase().includes(strUpper)) {
                  result = true;
                }
              }
            }
          }
          return result;
        });
      }

      setTotalNumber(filteredList.length);

      let newPage = page;
      //filter based on pagination
      const sliceStart = pageSize * (newPage - 1);
      const sliceEnd = pageSize * newPage > totalNumber ? totalNumber : pageSize * newPage;
      const newFilteredList = filteredList.slice(sliceStart, sliceEnd);

      setFilteredCollectionList(newFilteredList);
    };

    const handleChangePage = (event, newPage) => {
      setPage(newPage);
      handleFilterCollections(newPage, pageSize, searchStr);
    };

    const handleChangeRowsPerPage = (option) => {
      const newPageSize = parseInt(option.value, 10);
      setPageSize(newPageSize);
      setPage(1);
      handleFilterCollections(1, newPageSize, searchStr);
    };

    const pageCount = parseInt(Math.ceil(totalNumber / pageSize), 10);
    const pageOptions = [];
    for (let i = 0; i < pageCount; i++) {
      pageOptions.push(i);
    }
    const entriesStart = pageSize * (page - 1) + 1;
    const entriesEnd = pageSize * page > totalNumber ? totalNumber : pageSize * page;

    const handleSearchChange = (str) => {
      let formattedStr = "";
      if (str) {
        formattedStr = str.trim();
      }
      setSearchStr(formattedStr);
      handleFilterCollections(page, pageSize, formattedStr);
    };

    const handleOnSortChange = (e) => {
      setOrderOpt(e);
      let sortStr = e?.value;
      const newFilteredCollectionList = filteredCollectionList.sort((a, b) =>
        compare(a, b, sortStr)
      );
      setFilteredCollectionList(newFilteredCollectionList);
    };

    const retreiveCollectionImages = (col) => {
      setSelectedStyleImgs([]);
      let images = [];
      //should remove duplicate images. when style is duplicated from other place, the image url may same, we need to remove the duplicate
      let imageUrlSet = new Set();
      const products = col?.products || [];
      for (let i = 0; i < products.length; i++) {
        let styleImages = getStyleImages(products[i], false, metaFilesKey);
        styleImages = styleImages.filter((f) => f?.type && f.type.includes("image")); //remove pdf format
        for (let j = 0; j < styleImages.length; j++) {
          if (!imageUrlSet.has(styleImages[j]?.url)) {
            imageUrlSet.add(styleImages[j]?.url);
            images.push({
              img: styleImages[j],
              id: products[i].id,
              title: products[i].title,
            });
          }
        }
      }

      setCollectionImages(images);
    };

    return (
      <Dialog
        open={visible}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        maxWidth="xl"
      >
        <DialogTitle id="alert-dialog-title">
          Publish collection images to portfolio
          <IconButton
            aria-label="close"
            onClick={() => handleClose()}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent>
          <SuiBox>
            <SuiBox display="flex" alignItems="center" justifyContent="space-between" mb={2} mr={1}>
              {selectedStyleImgs.length > 0 ? (
                <SuiBox mt={1} ml={0.5} display="flex" itemsAlign="center" gap={1}>
                  <ImageIcon fontSize="small" color="text" />
                  <SuiTypography
                    component="label"
                    variant="body1"
                    textTransform="none"
                    color="text"
                  >
                    {selectedStyleImgs.length} images have been selected.
                  </SuiTypography>{" "}
                </SuiBox>
              ) : (
                <div />
              )}

              <SuiBox display="flex" alignItems="center" justifyContent="flex-end" gap={1}>
                <SuiBox>
                  <Card sx={{ borderRadius: 2, width: 180 }}>
                    <SuiSelect
                      options={collectionSortOptions}
                      placeholder="Sort"
                      isClearable
                      onChange={handleOnSortChange}
                      value={orderOpt}
                    />
                  </Card>
                </SuiBox>
                <SuiBox sx={{ width: 180 }}>
                  <SuiInput
                    placeholder="Search Keywords"
                    type="text"
                    onChange={(e) => handleSearchChange(e?.target?.value)}
                    icon={{
                      direction: "right",
                      component: (
                        <SearchIcon
                          sx={{ cursor: "pointer" }}
                          onClick={() => handleSearchChange(searchStr)}
                        />
                      ),
                    }}
                  />
                </SuiBox>
              </SuiBox>
            </SuiBox>
            <SuiBox display="flex" gap={2}>
              {collection?.id ? null : (
                <SuiBox>
                  <SuiTypography variant="h6">Collections</SuiTypography>
                  <SuiBox sx={{ maxHeight: 500, overflow: "auto" }}>
                    <SuiBox
                      display="flex"
                      justifyContent="flex-start"
                      flexDirection={collectionDetailVisible ? "column" : "row"}
                      sx={{ overflowY: "auto", flexWrap: "wrap" }}
                      gap={1}
                    >
                      {filteredCollectionList.map((col, index) => {
                        let moodboardImg = col?.moodboard?.[0]?.img;
                        return (
                          <SuiBox
                            key={index}
                            onClick={() => {
                              setSelectedCollection(collectionDetailVisible ? {} : col);
                              retreiveCollectionImages(collectionDetailVisible ? {} : col);
                              setCollectionDetailVisible(!collectionDetailVisible);
                            }}
                            sx={{
                              border: ({ borders }) =>
                                selectedCollection.id == col.id
                                  ? "2px solid rgb(34 211 238)"
                                  : `2px solid ${borders.borderColor}`,
                              boxShadow: ({ boxShadows: { md } }) =>
                                selectedCollection.id == col.id ? md : "none",
                              cursor: "pointer",
                            }}
                          >
                            <ImageCardWrapper
                              data={moodboardImg}
                              mode={"normal"}
                              height={200}
                              width={240}
                              iconMode
                            />
                            <SuiTypography
                              variant="h6"
                              sx={{
                                pt: 0.5,
                                pl: 0.5,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                display: "-webkit-box",
                                WebkitLineClamp: "1",
                                WebkitBoxOrient: "vertical",
                              }}
                            >
                              {col.title}
                            </SuiTypography>
                          </SuiBox>
                        );
                      })}
                    </SuiBox>
                  </SuiBox>
                </SuiBox>
              )}

              <SuiBox sx={{ flexGrow: 1 }}>
                {collectionDetailVisible ? (
                  <SuiTypography variant="h6">All images</SuiTypography>
                ) : null}

                {collectionDetailVisible ? (
                  collectionImages.length > 0 ? (
                    <SuiBox
                      display="flex"
                      justifyContent="flex-start"
                      sx={{
                        flexWrap: "wrap",
                        overflow: "auto",
                        width: "100%",
                      }}
                      gap={1}
                    >
                      {collectionImages.map((u, index) => {
                        const findResult = selectedStyleImgs.find(
                          (x) => x?.img?.url == u?.img?.url
                        );
                        let isSelected = findResult?.img ? true : false;

                        return (
                          <SuiBox
                            key={index}
                            position="relative"
                            onClick={() => {
                              if (isSelected) {
                                const removeImg = selectedStyleImgs.filter(
                                  (x) => x?.img?.url != u?.img?.url
                                );
                                setSelectedStyleImgs(removeImg);
                              } else {
                                setSelectedStyleImgs([...selectedStyleImgs, u]);
                              }
                            }}
                            sx={{
                              border: ({ borders }) =>
                                isSelected
                                  ? "2px solid #141414"
                                  : `2px solid ${borders.borderColor}`,
                              boxShadow: ({ boxShadows: { md } }) => (isSelected ? md : "none"),
                              height: 204,
                              cursor: "pointer",
                            }}
                          >
                            {" "}
                            <SuiBox
                              position="relative"
                              sx={{
                                overflow: "hidden",
                                backgroundImage: ({
                                  functions: { rgba, linearGradient },
                                  palette: { gradients },
                                }) =>
                                  `${linearGradient(
                                    rgba(gradients.secondary.main, 0.2),
                                    rgba(gradients.secondary.state, 0.4)
                                  )}`,
                                margin: 0,
                                boxShadow: ({ boxShadows: { md } }) => md,
                                objectFit: "contain",
                                objectPosition: "center",
                              }}
                            >
                              <ImageCardWrapper
                                data={u?.img}
                                mode={"normal"}
                                height={200}
                                width={160}
                                iconMode
                              />
                              <SuiBox
                                position="absolute"
                                top={8}
                                right={8}
                                display={isSelected ? "inline" : "none"}
                              >
                                <CheckCircleIcon color={"success"} />
                              </SuiBox>
                            </SuiBox>
                          </SuiBox>
                        );
                      })}
                    </SuiBox>
                  ) : (
                    <SuiBox>
                      {" "}
                      <Empty description={"No available images"} />
                    </SuiBox>
                  )
                ) : null}
              </SuiBox>
            </SuiBox>

            <SuiBox
              display="flex"
              flexDirection={{ xs: "column", sm: "row" }}
              justifyContent="space-between"
              alignItems={{ xs: "flex-start", sm: "center" }}
              p={3}
            >
              <SuiBox display="flex" alignItems="center">
                <SuiBox mb={{ xs: 3, sm: 0 }}>
                  <SuiTypography variant="button" color="secondary" fontWeight="regular">
                    Showing {entriesStart} to {entriesEnd} of {totalNumber} entries
                  </SuiTypography>
                </SuiBox>

                <SuiBox display="flex" alignItems="center" gap={1}>
                  <SuiSelect
                    defaultValue={{ value: pageSize, label: pageSize }}
                    options={[10, 20, 50].map((entry) => ({ value: entry, label: entry }))}
                    onChange={handleChangeRowsPerPage}
                    size="small"
                  />

                  <SuiTypography variant="caption" color="secondary">
                    entries per page
                  </SuiTypography>
                </SuiBox>
              </SuiBox>
              <Pagination
                variant={"outlined"}
                color={"info"}
                count={pageOptions.length}
                defaultPage={page}
                onChange={handleChangePage}
              />
            </SuiBox>
          </SuiBox>
        </DialogContent>
        <DialogActions>
          <SuiButton variant="gradient" size="medium" onClick={handleClose} color="light">
            Cancel
          </SuiButton>
          <SuiButton
            size="medium"
            variant="gradient"
            color="dark"
            onClick={() => handleOk(selectedCollection, selectedStyleImgs)}
          >
            {collection ? "Update" : "Import"}
          </SuiButton>
        </DialogActions>
      </Dialog>
    );
  }
);

export default ImportSingleCollectionModal;
